import { useState, type FormEvent } from 'react';
import Modal, { inputCls, labelCls } from '@/components/base/Modal';
import { saveCampaignMetric, computeCampaignKpis, type CampaignMetric } from '@/services/campaigns';

interface MetricModalProps {
  campaignId: string;
  campaignName: string;
  editing?: CampaignMetric | null;
  onClose: () => void;
  onSaved: () => void;
}

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

// Modal de captura del panel operativo. Registra los datos del periodo
// (inversión, conversaciones iniciadas, ventas/conversiones y facturación) y
// muestra en vivo los indicadores derivados (CPA, ROAS, % de cierre).
export default function MetricModal({
  campaignId,
  campaignName,
  editing,
  onClose,
  onSaved,
}: MetricModalProps) {
  const [entryDate, setEntryDate] = useState(
    editing?.entryDate ? editing.entryDate.slice(0, 10) : today(),
  );
  const [investment, setInvestment] = useState(editing ? String(editing.investment) : '');
  const [conversations, setConversations] = useState(editing ? String(editing.conversations) : '');
  const [sales, setSales] = useState(editing ? String(editing.sales) : '');
  const [revenue, setRevenue] = useState(editing ? String(editing.revenue) : '');
  const [notes, setNotes] = useState(editing?.notes ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const inv = Number(investment) || 0;
  const conv = Number(conversations) || 0;
  const sal = Number(sales) || 0;
  const rev = Number(revenue) || 0;
  const kpis = computeCampaignKpis({
    investment: inv,
    conversations: conv,
    sales: sal,
    revenue: rev,
  });

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');

    if (!entryDate) {
      setError('Selecciona la fecha del registro.');
      return;
    }
    if (sal > conv && conv > 0) {
      setError('Las ventas/conversiones no pueden ser mayores que las conversaciones iniciadas.');
      return;
    }

    setSaving(true);
    const { error: saveError } = await saveCampaignMetric({
      id: editing?.id,
      campaignId,
      entryDate,
      investment: inv,
      conversations: conv,
      sales: sal,
      revenue: rev,
      notes,
    });
    setSaving(false);

    if (saveError) {
      setError(saveError);
      return;
    }
    onSaved();
    onClose();
  }

  return (
    <Modal
      title={editing ? 'Editar registro de campaña' : 'Registrar datos de campaña'}
      subtitle={campaignName}
      onClose={onClose}
      maxWidth="max-w-lg"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2.5">
            <i className="ri-error-warning-line"></i>
            <span>{error}</span>
          </div>
        )}

        <div>
          <label className={labelCls}>Fecha del registro *</label>
          <input
            type="date"
            className={inputCls}
            value={entryDate}
            max={today()}
            onChange={(e) => setEntryDate(e.target.value)}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Inversión publicitaria ($)</label>
            <input
              type="number"
              min="0"
              className={inputCls}
              value={investment}
              onChange={(e) => setInvestment(e.target.value)}
              placeholder="Ej: 50000"
            />
          </div>
          <div>
            <label className={labelCls}>Conversaciones iniciadas</label>
            <input
              type="number"
              min="0"
              className={inputCls}
              value={conversations}
              onChange={(e) => setConversations(e.target.value)}
              placeholder="Ej: 35"
            />
          </div>
          <div>
            <label className={labelCls}>Ventas / conversiones</label>
            <input
              type="number"
              min="0"
              className={inputCls}
              value={sales}
              onChange={(e) => setSales(e.target.value)}
              placeholder="Ej: 8"
            />
          </div>
          <div>
            <label className={labelCls}>Facturación ($)</label>
            <input
              type="number"
              min="0"
              className={inputCls}
              value={revenue}
              onChange={(e) => setRevenue(e.target.value)}
              placeholder="Ej: 720000"
            />
          </div>
        </div>

        <div>
          <label className={labelCls}>Notas (opcional)</label>
          <textarea
            className={`${inputCls} resize-none`}
            rows={2}
            maxLength={300}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Observaciones del periodo..."
          />
        </div>

        <div className="grid grid-cols-3 gap-2 bg-background-50 border border-background-200 rounded-md p-3">
          <div className="text-center">
            <p className="text-[11px] text-foreground-500">CPA</p>
            <p className="text-sm font-bold text-foreground-950 mt-0.5">
              {kpis.cpa > 0 ? `$${Math.round(kpis.cpa).toLocaleString('es-CO')}` : '—'}
            </p>
          </div>
          <div className="text-center border-x border-background-200">
            <p className="text-[11px] text-foreground-500">ROAS</p>
            <p className="text-sm font-bold text-accent-600 mt-0.5">
              {kpis.roas > 0 ? `${kpis.roas.toFixed(2)}x` : '—'}
            </p>
          </div>
          <div className="text-center">
            <p className="text-[11px] text-foreground-500">% Cierre</p>
            <p className="text-sm font-bold text-foreground-950 mt-0.5">
              {kpis.conversion > 0 ? `${kpis.conversion.toFixed(1)}%` : '—'}
            </p>
          </div>
        </div>

        <div className="flex gap-3 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-2.5 text-sm border border-background-300 text-foreground-700 rounded-md hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={saving}
            className="flex-1 px-4 py-2.5 text-sm font-semibold bg-accent-500 text-white rounded-md hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {saving ? (
              <>
                <i className="ri-loader-4-line animate-spin mr-1.5"></i>Guardando...
              </>
            ) : (
              <>
                <i className="ri-save-line mr-1.5"></i>{editing ? 'Guardar cambios' : 'Registrar datos'}
              </>
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
}